/**
 * client/render/resolution.ts — render-resolution budget (pure, unit tested). OWNER: RENDER agent.
 *
 * ARCHITECTURE.md rule 8: 60+ fps at 1080p on a laptop. Retina laptops report devicePixelRatio 2, so the
 * default app pixel ratio (min(2, dpr) * renderScale) rendered the 4x-MSAA world pass at 2.2x the pixels
 * (perf-spikes.mjs UNCAP: DPR 2 = 6.4 ms p50 vs 3.3 ms at DPR 1 on an M3 Max; ESTIMATE ~26 fps on a base M1/M2).
 * The renderer therefore caps the drawing-buffer size at a pixel budget, but never below 1 device pixel per
 * CSS pixel (so non-Retina screens are unchanged) and never above what the app asked for (renderScale < 1
 * still lowers it). The budget is ESTIMATE: 1920x1080 pixels, i.e. the "1080p" of the performance rule.
 * Override: ?dpr=native (no cap), ?maxpx=<pixels>, or window.__render.setOption('maxPixels', n) (0 = no cap).
 */
export const DEFAULT_MAX_RENDER_PIXELS = 1920 * 1080;

export function effectivePixelRatio(cssWidth: number, cssHeight: number, requested: number, maxPixels: number): number {
  if (!(requested > 0)) return 1;
  if (!(maxPixels > 0)) return requested;
  const cap = Math.sqrt(maxPixels / Math.max(1, cssWidth * cssHeight));
  return Math.min(requested, Math.max(1, cap));
}

/** Budget from the page URL (?dpr=native -> 0 = uncapped, ?maxpx=N), else the default. */
export function maxPixelsFromQuery(search: string): number {
  const q = new URLSearchParams(search);
  if (q.get('dpr') === 'native') return 0;
  const n = Number(q.get('maxpx'));
  return Number.isFinite(n) && n >= 0 && q.has('maxpx') ? n : DEFAULT_MAX_RENDER_PIXELS;
}
